import clsx from 'clsx';
import React from 'react';
import { createPortal } from 'react-dom';
import styles from './FormFieldDropdown.module.scss';
import { FormFieldDropdownOption } from './interfaces';

type ListPosition = {
  top: number;
  left: number;
  width: number;
};

type FormFieldDropdownOptionsProps = {
  id: string;
  labelId?: string;
  options: FormFieldDropdownOption[];
  showOptions: boolean;
  selectedOptionIndex: number;
  portalList: boolean;
  listPosition: ListPosition | null;
  onClickOption: (option: FormFieldDropdownOption) => () => void;
};

const FormFieldDropdownOptions = React.forwardRef<
  HTMLUListElement,
  FormFieldDropdownOptionsProps
>(
  (
    {
      id,
      labelId,
      options,
      showOptions,
      selectedOptionIndex,
      portalList,
      listPosition,
      onClickOption,
    },
    ref
  ) => {
    const optionsList = (
      <ul
        aria-labelledby={labelId}
        className={clsx(styles.list, {
          [styles.isActive]: showOptions,
          [styles.withScrollbar]: options.length > 3,
        })}
        id={id}
        ref={ref}
        role="listbox"
        style={
          portalList && listPosition
            ? {
                position: 'fixed',
                top: listPosition.top,
                left: listPosition.left,
                width: listPosition.width,
                boxSizing: 'border-box',
                zIndex: 9999,
              }
            : undefined
        }
      >
        {options.map((option, index) => (
          <li
            aria-label={option.label}
            aria-selected={index === selectedOptionIndex}
            className={clsx(styles.listItem, {
              [styles.selected]: index === selectedOptionIndex,
            })}
            id={`${id}${index}`}
            onClick={onClickOption(option)}
            role="option"
            key={option.value}
          >
            {option.label}
          </li>
        ))}
      </ul>
    );

    if (portalList) {
      return showOptions && listPosition && typeof document !== 'undefined'
        ? createPortal(optionsList, document.body)
        : null;
    }

    return optionsList;
  }
);

FormFieldDropdownOptions.displayName = 'FormFieldDropdownOptions';

export default FormFieldDropdownOptions;
